import { useSyncExternalStore } from 'react';

// Tiny module-level pub/sub carrying canvas geometry OUT to CanvasScrollbars
// (a Diagram-level sibling, not a child of Canvas) and a small imperative
// pan API back IN — mirrors noteLinkHoverStore.js's shape
// (subscribe/getSnapshot through useSyncExternalStore, no redux) for the
// same reason: this is per-frame-ish geometry nothing outside the canvas
// area cares about, not persisted/collaborative/undoable state.
//
// Read side (Canvas -> CanvasScrollbars): `{ contentBounds, viewport }`,
// both world-space rects `{ minX, minY, maxX, maxY }`. contentBounds is
// null when there are no elements at all. Canvas publishes from two hooks
// (see Canvas.jsx): the Yjs elements-observer keeps contentBounds fresh
// only when element geometry actually changes (rare — O(elements) work),
// while every draw() tick recomputes+publishes the cheap O(1) viewport
// rect and re-sends the last-known contentBounds — so panning/zooming
// updates the published viewport live every frame without re-walking the
// elements map on frames where nothing moved.
//
// `publishCanvasScrollGeometry`'s `viewport` argument is allowed to be a
// REUSED mutable scratch object (Canvas.jsx recomputes it in place every
// draw() tick to avoid a fresh allocation every frame) — so this module
// clones it into a plain object of its own before ever storing/handing it
// to a subscriber. `getCanvasScrollGeometry()`'s return value is therefore
// always immutable-by-replacement: once handed out, a snapshot's fields
// never change under the reader; a real change publishes a WHOLE NEW
// object and notifies instead of mutating the previous one in place (the
// contract useSyncExternalStore's getSnapshot needs — see React docs on
// "the snapshot must be immutable").
//
// Write side (CanvasScrollbars -> Canvas): Canvas registers ONE api object
// on mount (registerCanvasScrollPanApi) — `{ applyDelta(axis, deltaOrigin),
// endGesture() }` — and unregisters on unmount (must pass back the exact
// token registerCanvasScrollPanApi returned; see there for why this is
// token-guarded, not a bare null-out).
//
// Why this crosses the component boundary via a module singleton instead
// of a forwarded ref: CanvasScrollbars is mounted as Canvas's SIBLING in
// Diagram.jsx (see App.js's route for /d/:diagramId — Diagram itself isn't
// remounted on a diagramId change, only <Canvas key={diagramId}> is), not
// its child, so there's no ref/prop path between them already, and adding
// one just for this would mean plumbing a ref through Diagram.jsx for a
// capability only this one overlay needs.

let geometry = null; // null | { contentBounds: {minX,minY,maxX,maxY} | null, viewport: {minX,minY,maxX,maxY} }
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener());

const rectEqual = (a, b) => {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.minX === b.minX && a.minY === b.minY && a.maxX === b.maxX && a.maxY === b.maxY;
};

export const getCanvasScrollGeometry = () => geometry;

export const subscribeCanvasScrollGeometry = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

// Canvas's publish side. `next.viewport` may be Canvas's reused scratch
// object (mutated again on the very next draw tick) — this function only
// ever reads its fields for the equality check and, on an actual change,
// COPIES them into a fresh plain object before storing. No-op (no notify,
// no clone) when nothing actually moved, so the very frequent draw()-tick
// call site doesn't churn React (or allocate) on every animation frame
// while idle/hovering.
export const publishCanvasScrollGeometry = (next) => {
    if (
        geometry &&
        rectEqual(geometry.contentBounds, next.contentBounds) &&
        rectEqual(geometry.viewport, next.viewport)
    ) {
        return;
    }
    const v = next.viewport;
    geometry = {
        // contentBounds already comes from computeContentBoundsFromElements
        // as a fresh, never-reused object (only replaced wholesale on a
        // structural element change) — safe to reference directly, no clone
        // needed.
        contentBounds: next.contentBounds,
        viewport: v ? { minX: v.minX, minY: v.minY, maxX: v.maxX, maxY: v.maxY } : v,
    };
    notify();
};

export const useCanvasScrollGeometry = () =>
    useSyncExternalStore(subscribeCanvasScrollGeometry, getCanvasScrollGeometry, () => null);

// Canvas's write-side API: { applyDelta(axis, deltaOrigin), endGesture() }.
// Token-guarded registration (not a bare last-write-wins slot):
// registerCanvasScrollPanApi returns a token the CALLER must hand back to
// unregisterCanvasScrollPanApi. A stale unregister (e.g. an old Canvas
// instance's unmount cleanup firing AFTER a newer instance already took
// over — shouldn't normally happen given effect ordering, but keeps the
// pairing airtight under StrictMode's synthetic mount/unmount/remount) is
// a no-op rather than clobbering the newer registration.
//
// The SAME token also guards every write call (applyCanvasScrollPan /
// endCanvasScrollPanGesture): CanvasScrollbars captures the token once, at
// the moment a gesture starts (mousedown / track click), and passes it
// back on every subsequent call for that gesture. If the registered API
// has changed identity in the meantime (a diagram switch remounted Canvas
// mid-drag — Diagram.jsx doesn't remount on a diagramId change, only
// <Canvas key={diagramId}> does, so CanvasScrollbars can outlive any one
// Canvas instance) the token no longer matches and the call is dropped —
// a stale drag can never apply its stray mousemove deltas to an unrelated
// diagram's Canvas, even in the narrow window where a NEW Canvas has
// already registered by the time the old gesture's next event fires.
let panApi = null;
let panApiToken = 0;

export const registerCanvasScrollPanApi = (api) => {
    panApiToken += 1;
    panApi = api;
    return panApiToken;
};

export const unregisterCanvasScrollPanApi = (token) => {
    if (token !== panApiToken) return; // stale unregister from a since-replaced instance
    panApi = null;
};

// CanvasScrollbars reads this once per gesture (mousedown / track click),
// not per event — see the module comment above.
export const getCanvasScrollPanToken = () => panApiToken;

// axis is 'x' | 'y', deltaOrigin is the amount to ADD to originXY on that
// axis (screen/origin-space units, see scrollbarMath.js for how a caller
// derives it from a pixel drag or a track click).
export const applyCanvasScrollPan = (axis, deltaOrigin, token) => {
    if (token !== panApiToken) return;
    panApi?.applyDelta(axis, deltaOrigin);
};

// Gesture-end signal (mouseup, or the drag component's own unmount
// cleanup): clears isBoardMoving/isBoardMovingStarted immediately instead
// of waiting out the 200ms inactivity timer the per-tick applyDelta path
// also refreshes — matches the immediate clear the native board-drag path
// gets on its own mouseup (unsetAllVariablesOnClick).
export const endCanvasScrollPanGesture = (token) => {
    if (token !== panApiToken) return;
    panApi?.endGesture();
};
